/**
 * Monthly capacity: what has been used, and how to buy more.
 *
 * The plan sells locations and monthly transactions, so this is the screen a
 * customer actually watches. It does two things, deliberately in this order:
 * it shows where they stand, and only then offers to sell them more.
 *
 * ── Why the steps ──────────────────────────────────────────────────────────
 *
 * 80%, 95% and 100% say different things to the person reading them — plan,
 * decide this week, and capacity is used — and a single bar going red at some
 * unstated point says none of them. The thresholds come from the server with
 * the figure, so the bar and the prompt can never disagree.
 *
 * Nothing here refuses anything. Passing 100% does not stop a sale or a
 * delivery being recorded; it means the next one goes over the allowance, and
 * the business should decide whether to buy capacity or change plan.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USAGE_THRESHOLDS } from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Badge, Button, Card, CardHeader, Input, Spinner } from '../../components/ui';
import { currencyPrefix } from '../../lib/format';

interface UsageLine {
  metric: string;
  label: string;
  kind: 'level' | 'counter';
  used: number;
  limit: number | null;
  purchased: number;
  enforcement: 'block' | 'warn' | 'meter';
  percentUsed: number | null;
  threshold: 80 | 95 | 100 | null;
  overLimit: boolean;
}

interface UsageSummary {
  planName: string;
  periodStart: string;
  periodEnd: string;
  lines: UsageLine[];
}

interface PlanLimit {
  metric: string;
  limit: number | null;
  overageUnitSize: number | null;
  overageAmount: string | null;
  overageCurrencyCode: string | null;
}

interface Plan {
  key: string;
  limits?: PlanLimit[];
}

const count = (n: number) => n.toLocaleString();

/**
 * Bytes, as a person reads them.
 *
 * Powers of 1024 and the same unit on both sides of "of", so "80 GB of 100 GB"
 * is a comparison rather than two numbers in different units. Printing the raw
 * figure would have put "85,899,345,920 of 107,374,182,400" on a page whose
 * whole job is telling somebody how much room they have left.
 */
function bytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below 10, none above: 1.4 GB is useful, 85.9 GB is noise.
  const digits = unit === 0 ? 0 : value < 10 ? 1 : 0;
  return `${value.toLocaleString(undefined, { maximumFractionDigits: digits })} ${units[unit]}`;
}

/** How this metric's figures should read. */
const shownAs = (metric: string) => (metric === 'storage_bytes' ? bytes : count);

/** What the bar looks like at each step. Never silent about the last one. */
function tone(line: UsageLine): { bar: string; badge: ReactNode } {
  if (line.threshold === 100) {
    return {
      bar: 'bg-red-500',
      badge: <Badge tone="danger">Allowance used</Badge>,
    };
  }
  if (line.threshold === 95) {
    return { bar: 'bg-amber-500', badge: <Badge tone="warning">95% used</Badge> };
  }
  if (line.threshold === 80) {
    return { bar: 'bg-amber-400', badge: <Badge tone="info">80% used</Badge> };
  }
  return { bar: 'bg-emerald-500', badge: null };
}

function UsageBar({ line }: { line: UsageLine }): ReactNode {
  const { bar, badge } = tone(line);
  const show = shownAs(line.metric);
  // Capped at 100 for the WIDTH only. The number beside it is the real one —
  // a bar that cannot show more than full must not be the only thing reporting.
  const width = Math.min(100, line.percentUsed ?? 0);

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-slate-700">{line.label}</span>
        <span className="text-sm text-slate-600">
          {line.limit === null ? (
            <>{show(line.used)} · no limit</>
          ) : (
            <>
              {show(line.used)} of {show(line.limit)}
              {line.purchased > 0 && (
                <span className="text-slate-500">
                  {' '}
                  ({show(line.limit - line.purchased)} on your plan + {show(line.purchased)}{' '}
                  bought)
                </span>
              )}
            </>
          )}{' '}
          {badge}
        </span>
      </div>
      {line.limit !== null && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${bar}`} style={{ width: `${width}%` }} />
        </div>
      )}
      {line.overLimit && line.enforcement === 'warn' && (
        <p className="text-xs text-slate-600">
          Past the allowance. Nothing has been refused — records are still being saved — and buying
          extra capacity or changing plan brings this back under.
        </p>
      )}
    </div>
  );
}

export function CapacityCard(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const [packages, setPackages] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<string | null>(null);

  const usage = useQuery({
    queryKey: ['usage', business?.businessId],
    queryFn: () => api.get<UsageSummary>(path('/usage')),
    enabled: Boolean(business),
  });

  const plans = useQuery({
    queryKey: ['plans', business?.businessId, null],
    queryFn: () => api.get<{ data: Plan[] }>(path('/plans')),
    enabled: Boolean(business),
  });

  const subscription = useQuery({
    queryKey: ['subscription', business?.businessId],
    queryFn: () => api.get<{ planKey: string }>(path('/subscription')),
    enabled: Boolean(business),
  });

  const buy = useMutation({
    mutationFn: (n: number) =>
      api.post<{ paymentUrl: string | null; instructions?: string | null; message: string }>(
        path('/subscription/transactions'),
        // Acknowledged here because the price is shown on the button that
        // sends it — see the server, which refuses an unconfirmed purchase.
        { packages: n, acknowledge: true },
      ),
    onSuccess: (result) => {
      setError(null);
      if (result.paymentUrl) {
        window.location.assign(result.paymentUrl);
        return;
      }
      setInstructions(result.instructions ?? result.message);
      for (const key of ['usage', 'invoices']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not open the invoice'),
  });

  if (usage.isPending) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }
  if (usage.isError || !usage.data) return null;

  const summary = usage.data;

  /*
    The three lines a plan is sold on, in the order a customer thinks about
    them. AI tokens and conversations are metered consumption with their own
    card and their own invoice line; listing all eight metrics here turned a
    capacity screen into a diagnostics table.

    Storage is here rather than on the AI card because it is an allowance like
    the other two — a fixed amount that runs out — and because running out of
    it refuses an upload, which somebody should see coming at 80% rather than
    discover at 100%.
  */
  const shown = ['transactions', 'branches', 'storage_bytes']
    .map((metric) => summary.lines.find((l) => l.metric === metric))
    .filter((l): l is UsageLine => Boolean(l));

  const storage = summary.lines.find((l) => l.metric === 'storage_bytes') ?? null;

  const transactions = summary.lines.find((l) => l.metric === 'transactions') ?? null;

  const currentPlan = plans.data?.data.find((p) => p.key === subscription.data?.planKey);
  const rate = currentPlan?.limits?.find((l) => l.metric === 'transactions') ?? null;
  const unitSize = rate?.overageUnitSize ?? null;
  const unitPrice = rate?.overageAmount ? Number(rate.overageAmount) : null;
  const currency = rate?.overageCurrencyCode ?? null;

  const wanted = Math.max(1, Math.min(100, Math.trunc(Number(packages) || 0)));
  const canBuy =
    can('settings:update') &&
    transactions !== null &&
    transactions.limit !== null &&
    unitSize !== null &&
    unitPrice !== null &&
    currency !== null;

  const total = unitPrice !== null ? unitPrice * wanted : null;

  const period = `${new Date(summary.periodStart).toLocaleDateString()} – ${new Date(
    summary.periodEnd,
  ).toLocaleDateString()}`;

  return (
    <Card>
      <CardHeader
        title="This month's capacity"
        subtitle={`${summary.planName} plan · ${period}`}
      />
      <div className="space-y-5 p-4 pt-0">
        <div className="space-y-4">
          {shown.map((line) => (
            <UsageBar key={line.metric} line={line} />
          ))}
        </div>

        {transactions?.limit === null ? (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            This plan has no transaction limit, so there is no extra capacity to buy.
          </p>
        ) : canBuy ? (
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-medium text-slate-700">Buy extra transactions</p>
            <p className="text-sm text-slate-600">
              {count(unitSize!)} extra transactions for{' '}
              {currencyPrefix(currency)}{unitPrice!.toLocaleString(undefined, { minimumFractionDigits: 2 })}, for
              this month only. Your allowance goes up when the invoice is paid, not before.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm text-slate-600">
                Packages
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={packages}
                  onChange={(e) => setPackages(e.target.value)}
                  className="mt-1 w-24"
                />
              </label>
              <Button disabled={buy.isPending} onClick={() => buy.mutate(wanted)}>
                {/* The price is on the button, which is what the customer is
                    confirming when they press it. */}
                Buy {count(wanted * unitSize!)} transactions — {currencyPrefix(currency)}
                {total!.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Button>
            </div>
            {/* Honest about the ceiling rather than silently clamping. */}
            <p className="text-xs text-slate-500">
              Up to 100 packages at a time. For more, or for a different allowance every month,
              talk to us about a plan or a contract.
            </p>
          </div>
        ) : (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {can('settings:update')
              ? 'Extra transactions are not currently sold for this account.'
              : 'An administrator can buy extra transactions for this month.'}
          </p>
        )}

        {instructions && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">{instructions}</p>
        )}
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        )}

        {storage?.threshold && (
          <p
            className={`rounded-lg px-3 py-2 text-sm ${
              storage.threshold === 100 ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'
            }`}
          >
            {storage.threshold === 100
              ? 'Storage is full. New receipts cannot be attached until some are removed.'
              : `Storage is ${storage.threshold}% used. Attaching receipts stops when it is full.`}{' '}
            {/* The carve-out, where somebody worried about it is actually looking. */}
            Proof of payment is never refused, so recording a delivery as paid keeps working.
          </p>
        )}

        <p className="text-xs text-slate-500">
          A transaction is one sale or one delivery recorded this month, across every location.
          Imported history, voided sales and superseded duplicates do not count, and nothing is
          ever refused for being over the allowance. Storage is what is held now — deleting a file
          gives the room back. You are told at {USAGE_THRESHOLDS.join('%, ')}%.
        </p>
      </div>
    </Card>
  );
}
